import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import Navbar from '../components/Navbar';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/Footer';
import {
  Star,
  Heart,
  Share2,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  Users,
  MapPin,
  UtensilsCrossed,
  Check,
  Loader2,
  CalendarDays,
  Play,
  Copy,
  Mail,
  MessageCircle,
  Link2,
  Award,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExperienceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const { formatPrice } = useCurrency();
  const isAuthenticated = !!token;
  const [experience, setExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [guests, setGuests] = useState(1);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showMobileBooking, setShowMobileBooking] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    special_requests: ''
  });
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const getShareData = () => ({
    title: experience?.title || 'Check out this meal experience!',
    text: `${experience?.title} — ${experience?.cuisine} experience in ${experience?.location}. ${formatPrice(experience?.price)}/person`,
    url: window.location.href,
  });

  const handleShare = async () => {
    const shareData = getShareData();
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShareMenu(true);
        }
      }
    } else {
      setShowShareMenu(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
    setShowShareMenu(false);
  };

  const shareVia = (platform) => {
    const { title, text, url } = getShareData();
    const encoded = encodeURIComponent(url);
    
    // Format messages with URL on a separate line for better clickability
    const emailBody = encodeURIComponent(`${text}\n\nCheck it out here:\n${url}`);
    const socialText = encodeURIComponent(`${text}\n\n${url}`);
    
    const links = {
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${emailBody}`,
      x: `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encoded}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}&quote=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${socialText}`,
    };
    window.open(links[platform], '_blank', 'noopener,noreferrer');
    setShowShareMenu(false);
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) return;
    
    if (!isAuthenticated) {
      toast.error('Please log in to message the host');
      navigate('/login');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/api/messages`, {
        experience_id: id,
        recipient_id: experience.host_id,
        content: messageContent.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Message sent to host!');
      setShowMessageModal(false);
      setMessageContent('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    fetchExperience();
    fetchReviews();
    // Track browse history for logged-in users
    if (token && id) {
      axios.post(`${API_URL}/api/browse-history/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    // Check if user can review (logged in, completed experience, and hasn't reviewed yet)
    if (isAuthenticated && token && id) {
      checkCanReview();
    }
  }, [isAuthenticated, token, id, reviews]);

  const checkCanReview = async () => {
    try {
      // Use the new backend endpoint to check if user can review
      const res = await axios.get(`${API_URL}/api/experiences/${id}/can-review`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCanReview(res.data.can_review);
    } catch {
      setCanReview(false); // Default to not allowing if check fails
    }
  };

  useEffect(() => {
    fetchExperience();
  }, [id]);

  const fetchExperience = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/experiences/${id}`);
      setExperience(response.data);
    } catch (error) {
      console.error('Failed to fetch experience:', error);
      setExperience(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/experiences/${id}/reviews`);
      setReviews(res.data);
    } catch {
      setReviews([]);
    }
  };

  const submitReview = async () => {
    if (!reviewForm.comment.trim()) return toast.error('Please write a review');
    setReviewLoading(true);
    try {
      await axios.post(`${API_URL}/api/reviews`, {
        experience_id: id,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review submitted! It will appear once approved by an admin.');
      setReviewForm({ rating: 5, comment: '' });
      setCanReview(false);
      fetchReviews();
      fetchExperience();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  // Get available dates from the experience
  const getAvailableDates = () => {
    if (!experience?.availability) return [];
    
    const dates = [];
    const today = new Date();
    
    // Add specific dates
    if (experience.availability.specific_dates) {
      experience.availability.specific_dates.forEach(slot => {
        const date = new Date(slot.date);
        if (date >= today) {
          dates.push(date);
        }
      });
    }
    
    // Add recurring dates for the next 3 months
    if (experience.availability.recurring) {
      const daysMap = {
        'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
        'Thursday': 4, 'Friday': 5, 'Saturday': 6
      };
      
      const recurringDays = experience.availability.recurring.days.map(d => daysMap[d]);
      
      for (let i = 0; i < 90; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        if (recurringDays.includes(date.getDay())) {
          dates.push(date);
        }
      }
    }
    
    return dates;
  };

  const availableDates = getAvailableDates();

  const isDateAvailable = (date) => {
    return availableDates.some(d => 
      d.toDateString() === date.toDateString()
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
      </div>
    );
  }

  if (!experience) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="experience-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Experience not found</h2>
          <Button onClick={() => navigate('/')} variant="outline" data-testid="go-home-btn">
            Go back home
          </Button>
        </div>
      </div>
    );
  }

  const images = experience.images?.length > 0 
    ? experience.images 
    : ['https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800'];

  const totalPrice = guests * experience.price;
  const serviceFee = Math.round(totalPrice * 0.10);
  const total = totalPrice + serviceFee;

  const handleReserve = () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    if (!isAuthenticated) {
      navigate('/register', { state: { from: `/experience/${id}`, intent: 'guest' } });
      return;
    }
    // Auto-fill from logged-in user
    if (user) {
      setBookingForm(prev => ({
        ...prev,
        guest_name: prev.guest_name || user.name || '',
        guest_email: prev.guest_email || user.email || '',
      }));
    }
    setShowBookingForm(true);
  };

  const submitBooking = async () => {
    if (!bookingForm.guest_name || !bookingForm.guest_email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setBookingLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/payments/create-checkout`, {
        experience_id: experience.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        guests: guests,
        guest_name: bookingForm.guest_name,
        guest_email: bookingForm.guest_email,
        guest_phone: bookingForm.guest_phone,
        special_requests: bookingForm.special_requests,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment session');
    } finally {
      setBookingLoading(false);
    }
  };

  const getTimeSlot = () => {
    if (!selectedDate || !experience.availability) return null;
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    // Check specific dates first
    const specificSlot = experience.availability.specific_dates?.find(s => s.date === dateStr);
    if (specificSlot) return specificSlot;
    
    // Check recurring
    if (experience.availability.recurring) {
      const dayName = format(selectedDate, 'EEEE');
      if (experience.availability.recurring.days.includes(dayName)) {
        return experience.availability.recurring;
      }
    }
    
    return null;
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const timeSlot = selectedDate ? getTimeSlot() : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="experience-detail-page">
      <Navbar />
      <div className="h-20" />
      <Toaster />

      <main className="flex-1 max-w-7xl mx-auto px-6 md:px-10 lg:px-20 py-6">
        {/* Title Section */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold mb-2" data-testid="experience-title">{experience.title}</h1>
          {experience.rating >= 4.0 && experience.review_count >= 1 && (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5C7A3A] bg-[#5C7A3A]/10 px-3 py-1 rounded-full mb-3" data-testid="local-favourite-detail-badge">
              <Award className="w-4 h-4" /> Local Favourite
            </span>
          )}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-gray-900" />
                <span className="font-medium">{experience.rating?.toFixed(1) || 'New'}</span>
              </div>
              <span className="text-gray-500">·</span>
              <span>{experience.review_count || 0} reviews</span>
              <span className="text-gray-500">·</span>
              <span>{experience.location}</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors"
                data-testid="share-button"
              >
                <Share2 className="w-4 h-4" />
                <span className="underline text-sm font-medium">Share</span>
              </button>
              <button
                onClick={() => setIsLiked(!isLiked)}
                className="flex items-center gap-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors"
              >
                <Heart className={`w-4 h-4 ${isLiked ? 'fill-[#D65A31] text-[#D65A31]' : ''}`} />
                <span className="underline text-sm font-medium">Save</span>
              </button>
            </div>
          </div>
        </div>

        {/* Photo Gallery - Mobile optimized */}
        <div
          className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 rounded-xl overflow-hidden mb-8 cursor-pointer"
          onClick={() => setShowAllPhotos(true)}
        >
          {/* Main image - full width on mobile */}
          <div className="md:col-span-2 md:row-span-2 relative h-64 md:h-[400px]">
            <img
              src={images[0]}
              alt={experience.title}
              className="w-full h-full object-cover hover:opacity-90 transition-opacity"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800';
              }}
            />
            {experience.video && (
              <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2">
                <Play className="w-4 h-4 fill-white" />
                Watch Video
              </div>
            )}
            {/* Show all photos button on mobile */}
            <button className="md:hidden absolute bottom-4 right-4 bg-white px-4 py-2 rounded-lg text-sm font-medium shadow-md hover:bg-gray-100">
              Show all {images.length} photos
            </button>
          </div>
          {/* Secondary images - hidden on mobile */}
          {images.slice(1, 5).map((image, index) => (
            <div key={image || `img-${index}`} className="relative hidden md:block h-[196px]">
              <img
                src={image}
                alt={`${experience.title} ${index + 2}`}
                className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800';
                }}
              />
              {index === 3 && images.length > 5 && (
                <button className="absolute bottom-4 right-4 bg-white px-4 py-2 rounded-lg text-sm font-medium shadow-md hover:bg-gray-100">
                  Show all photos
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Details */}
          <div className="lg:col-span-2">
            {/* Host Info - Mobile optimized */}
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pb-6 border-b">
              <div className="flex-1">
                <h2 className="text-lg sm:text-xl font-semibold mb-1">
                  {experience.cuisine} experience hosted by {experience.host_name}
                </h2>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {experience.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Up to {experience.max_guests} guests
                  </span>
                  <span className="flex items-center gap-1">
                    <UtensilsCrossed className="w-4 h-4" />
                    {experience.meal_type}
                  </span>
                </div>
              </div>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-200 flex items-center justify-center text-lg sm:text-xl font-semibold text-gray-600 flex-shrink-0">
                {experience.host_name?.charAt(0) || 'H'}
              </div>
            </div>

            {/* Description */}
            <div className="py-8 border-b">
              <h3 className="text-xl font-semibold mb-4">About this experience</h3>
              <p className="text-gray-600 whitespace-pre-line">{experience.description}</p>
            </div>

            {/* Video Section */}
            {experience.video && (
              <div className="py-8 border-b">
                <h3 className="text-xl font-semibold mb-4">Video Preview</h3>
                <video 
                  src={experience.video} 
                  controls 
                  className="w-full max-w-2xl rounded-xl"
                  poster={images[0]}
                />
              </div>
            )}

            {/* What's included */}
            {experience.amenities?.length > 0 && (
              <div className="py-8 border-b">
                <h3 className="text-xl font-semibold mb-4">What's included</h3>
                <div className="grid grid-cols-2 gap-4">
                  {experience.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#D65A31]" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Availability */}
            {experience.availability && (
              <div className="py-8 border-b">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  Availability
                </h3>
                {experience.availability.recurring && (
                  <p className="text-gray-600 mb-2">
                    <strong>Weekly:</strong> {experience.availability.recurring.days.join(', ')} at {formatTime(experience.availability.recurring.start_time)} - {formatTime(experience.availability.recurring.end_time)}
                  </p>
                )}
                {experience.availability.specific_dates?.length > 0 && (
                  <div>
                    <strong className="text-gray-600">Specific dates:</strong>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {experience.availability.specific_dates.slice(0, 6).map((slot) => (
                        <span key={slot.date + slot.start_time} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                          {format(new Date(slot.date), 'MMM d')} at {formatTime(slot.start_time)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Section */}
            <div className="py-8" data-testid="reviews-section">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Star className="w-5 h-5" />
                Reviews ({reviews.length})
                {experience.rating > 0 && (
                  <span className="text-base font-normal text-gray-500">
                    - {experience.rating?.toFixed(1)} average
                  </span>
                )}
              </h3>

              {/* Write a review */}
              {canReview && (
                <div className="mb-6 p-4 border rounded-xl bg-gray-50" data-testid="review-form">
                  <h4 className="font-medium mb-3">Write a review</h4>
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        data-testid={`review-star-${star}`}
                      >
                        <Star
                          className={`w-6 h-6 cursor-pointer transition-colors ${
                            star <= reviewForm.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <Textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Share your experience..."
                    rows={3}
                    className="mb-3"
                    data-testid="review-comment-input"
                  />
                  <Button
                    onClick={submitReview}
                    disabled={reviewLoading}
                    className="bg-[#D65A31] hover:bg-[#B84A28] text-white"
                    data-testid="submit-review-btn"
                  >
                    {reviewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Submit Review
                  </Button>
                </div>
              )}

              {reviews.length === 0 ? (
                <p className="text-gray-400" data-testid="no-reviews">No reviews yet. Be the first!</p>
              ) : (
                <div className="space-y-5" data-testid="reviews-list">
                  {reviews.map((r) => (
                    <div key={r.id} className="flex gap-4" data-testid={`review-${r.id}`}>
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600 shrink-0">
                        {r.user_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{r.user_name}</span>
                          <span className="text-xs text-gray-400">
                            {r.created_at && format(new Date(r.created_at), 'MMM yyyy')}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5 mb-1">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star
                              key={s}
                              className={`w-3.5 h-3.5 ${s <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`}
                            />
                          ))}
                        </div>
                        <p className="text-sm text-gray-600">{r.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Booking Card (Hidden on mobile, shown on desktop) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 border rounded-xl p-6 shadow-lg bg-white">
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-2xl font-semibold">{formatPrice(experience.price)}</span>
                <span className="text-gray-500">/ person</span>
              </div>

              {/* Date Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Select Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;
                        if (availableDates.length > 0 && !isDateAvailable(date)) return true;
                        return false;
                      }}
                      modifiers={{
                        available: availableDates
                      }}
                      modifiersStyles={{
                        available: { fontWeight: 'bold', color: '#D65A31' }
                      }}
                    />
                  </PopoverContent>
                </Popover>
                {selectedDate && timeSlot && (
                  <p className="text-sm text-gray-600 mt-2">
                    Time: {formatTime(timeSlot.start_time)} - {formatTime(timeSlot.end_time)}
                  </p>
                )}
              </div>

              {/* Guest Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Guests</label>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGuests(Math.max(1, guests - 1))}
                    disabled={guests <= 1}
                  >
                    -
                  </Button>
                  <span className="font-medium w-8 text-center">{guests}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGuests(Math.min(experience.max_guests, guests + 1))}
                    disabled={guests >= experience.max_guests}
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* Reserve Button */}
              <Button
                onClick={handleReserve}
                className="w-full bg-[#FF6B35] hover:bg-[#E85A25] text-white py-6 text-lg"
              >
                Reserve
              </Button>

              {/* Message Host Button */}
              {experience.host_id !== user?.id && (
                <Button
                  onClick={() => setShowMessageModal(true)}
                  variant="outline"
                  className="w-full mt-3 py-5"
                  data-testid="message-host-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Message Host
                </Button>
              )}

              {/* Price Breakdown */}
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="underline">{formatPrice(experience.price)} x {guests} guest{guests > 1 ? 's' : ''}</span>
                  <span>{formatPrice(totalPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="underline">Service fee</span>
                  <span>{formatPrice(serviceFee)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t font-semibold">
                  <span>Total</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Sticky Footer for Booking */}
      {!showMobileBooking && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-semibold">{formatPrice(experience.price)}</span>
                <span className="text-gray-500 text-sm">/ person</span>
              </div>
              <p className="text-sm text-gray-500">
                {selectedDate ? format(selectedDate, 'MMM d') : 'Select date'} · {guests} guest{guests !== 1 ? 's' : ''}
              </p>
            </div>
            <Button
              onClick={() => setShowMobileBooking(true)}
              className="bg-[#FF6B35] hover:bg-[#E85A25] text-white px-8 py-3"
            >
              Reserve
            </Button>
          </div>
        </div>
      )}

      {/* Mobile Booking Sheet */}
      {showMobileBooking && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setShowMobileBooking(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-semibold">Book this experience</h3>
              <button onClick={() => setShowMobileBooking(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            {/* Date Selection */}
            <div className="mb-5">
              <label className="block text-sm font-medium mb-2">Select Date</label>
              <div className="border rounded-lg overflow-hidden">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (date < today) return true;
                    if (availableDates.length > 0 && !isDateAvailable(date)) return true;
                    return false;
                  }}
                  modifiers={{ available: availableDates }}
                  modifiersStyles={{ available: { fontWeight: 'bold', color: '#D65A31' } }}
                  className="w-full"
                />
              </div>
              {selectedDate && timeSlot && (
                <p className="text-sm text-gray-600 mt-2">
                  Time: {formatTime(timeSlot.start_time)} - {formatTime(timeSlot.end_time)}
                </p>
              )}
            </div>

            {/* Guest Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Guests</label>
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={() => setGuests(Math.max(1, guests - 1))} disabled={guests <= 1}>-</Button>
                <span className="font-medium w-8 text-center">{guests}</span>
                <Button variant="outline" size="sm" onClick={() => setGuests(Math.min(experience.max_guests, guests + 1))} disabled={guests >= experience.max_guests}>+</Button>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="space-y-2 text-sm mb-5 border-t pt-4">
              <div className="flex justify-between">
                <span>{formatPrice(experience.price)} x {guests} guest{guests > 1 ? 's' : ''}</span>
                <span>{formatPrice(totalPrice)}</span>
              </div>
              <div className="flex justify-between">
                <span>Service fee</span>
                <span>{formatPrice(serviceFee)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t font-semibold">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
            </div>

            <Button
              onClick={() => { setShowMobileBooking(false); handleReserve(); }}
              className="w-full bg-[#FF6B35] hover:bg-[#E85A25] text-white py-6 text-lg"
            >
              Reserve
            </Button>
          </div>
        </div>
      )}

      {/* Add padding at bottom for mobile sticky footer */}
      <div className="lg:hidden h-24" />

      <Footer />

      {/* Photo Gallery Modal */}
      <Dialog open={showAllPhotos} onOpenChange={setShowAllPhotos}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-auto p-0 overflow-hidden">
          <DialogTitle className="sr-only">Photo Gallery</DialogTitle>
          <DialogDescription className="sr-only">Browse all photos for this experience</DialogDescription>
          <div className="relative w-full h-[90vh] bg-black flex items-center justify-center">
            <button
              onClick={() => setShowAllPhotos(false)}
              className="absolute top-4 left-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            <img
              src={images[currentPhotoIndex]}
              alt={`Photo ${currentPhotoIndex + 1}`}
              className="max-w-[90%] max-h-[85vh] object-contain"
            />
            
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev - 1 + images.length) % images.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full p-2 hover:bg-black/70"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev + 1) % images.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full p-2 hover:bg-black/70"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-3 py-1 rounded-full text-sm">
              {currentPhotoIndex + 1} / {images.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Form Modal */}
      <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
        <DialogContent className="max-w-md">
          <DialogTitle className="text-xl font-semibold mb-4">Complete Your Booking</DialogTitle>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <Input
                value={bookingForm.guest_name}
                onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <Input
                type="email"
                value={bookingForm.guest_email}
                onChange={(e) => setBookingForm({ ...bookingForm, guest_email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Phone (optional)</label>
              <Input
                value={bookingForm.guest_phone}
                onChange={(e) => setBookingForm({ ...bookingForm, guest_phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Special Requests</label>
              <Textarea
                value={bookingForm.special_requests}
                onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                placeholder="Dietary restrictions, allergies, etc."
                rows={3}
              />
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium">{experience.title}</p>
              <p className="text-sm text-gray-600">
                {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                {timeSlot && ` at ${formatTime(timeSlot.start_time)}`}
              </p>
              <p className="text-sm text-gray-600">{guests} guest{guests > 1 ? 's' : ''}</p>
              <p className="font-semibold mt-2">Total: {formatPrice(total)}</p>
            </div>
            
            <Button
              onClick={submitBooking}
              disabled={bookingLoading}
              className="w-full bg-[#FF6B35] hover:bg-[#E85A25] text-white"
              data-testid="pay-and-reserve-btn"
            >
              {bookingLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
              ) : (
                'Pay & Reserve'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Menu Dialog */}
      <Dialog open={showShareMenu} onOpenChange={setShowShareMenu}>
        <DialogContent className="max-w-sm" data-testid="share-dialog">
          <DialogTitle className="text-lg font-semibold mb-4">Share this experience</DialogTitle>
          <DialogDescription className="sr-only">Choose how to share this experience</DialogDescription>
          <div className="space-y-1">
            <button
              onClick={copyLink}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="share-copy-link"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-gray-700" />
              </div>
              <span className="font-medium text-sm">Copy link</span>
            </button>
            <button
              onClick={() => shareVia('email')}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="share-email"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-gray-700" />
              </div>
              <span className="font-medium text-sm">Email</span>
            </button>
            <button
              onClick={() => shareVia('whatsapp')}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="share-whatsapp"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="font-medium text-sm">WhatsApp</span>
            </button>
            <button
              onClick={() => shareVia('x')}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="share-x"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </div>
              <span className="font-medium text-sm">X (Twitter)</span>
            </button>
            <button
              onClick={() => shareVia('facebook')}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="share-facebook"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </div>
              <span className="font-medium text-sm">Facebook</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Host Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="max-w-md">
          <DialogTitle className="text-xl font-semibold">Message Host</DialogTitle>
          <DialogDescription className="text-gray-500">
            Send a message to {experience?.host_name} about "{experience?.title}"
          </DialogDescription>
          
          <div className="mt-4">
            <Textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Hi! I have a question about your experience..."
              rows={5}
              maxLength={2000}
              className="w-full"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">
              {messageContent.length}/2000
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowMessageModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendMessage}
              disabled={!messageContent.trim() || sendingMessage}
              className="bg-[#D65A31] hover:bg-[#B84A28] text-white"
            >
              {sendingMessage ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <MessageCircle className="w-4 h-4 mr-2" />
              )}
              Send Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExperienceDetailPage;
